import moment, { isMoment } from 'moment';
import * as FeatureType from '../constants/featureTypes';
import * as StorageKey from '../constants/storageKeys';
import * as TenantKey from '../constants/tenantSettingKeys';
import * as fth from '../utilities/featureHelper';
import * as fh from './formatHelper';

export const configuration = {
    get preBetaRelease() {
        const isEnabled = getBooleanValue(TenantKey.PRE_BETA_RELEASE, false);
        return isEnabled;
    },
    daySheet: {
        get fields() {
            if (fth.check(FeatureType.APPOINTMENT)) {
                const fields = getArrayValue(TenantKey.DAY_SHEET_FIELDS);
                return fields && fields.length > 0 ? fields : [];
            }
            return [];
        },
        get orientation() {
            if (fth.check(FeatureType.APPOINTMENT)) {
                const orientation = getValue(TenantKey.DAY_SHEET_ORIENTATION);
                return orientation && ['portrait', 'landscape'].some(o => o === orientation.toLowerCase()) ? orientation.toLowerCase() : 'landscape';
            }
            return 'landscape';
        }
    },
    customer: {
        get requiredEmailCreation() {
            if (fth.check(FeatureType.CUSTOMER)) {
                const isRequired = getBooleanValue(TenantKey.PATIENT_CREATE_EMAIL_REQUIRED, false);
                return isRequired;
            }
            return false;
        },
        get requiredPhoneCreation() {
            if (fth.check(FeatureType.CUSTOMER)) {
                const isRequired = getBooleanValue(TenantKey.PATIENT_CREATE_PHONE_REQUIRED, false);
                return isRequired;
            }
            return false;
        },
        get spokenLanguages() {
            if (fth.check(FeatureType.CUSTOMER)) {
                const languages = getArrayValue(TenantKey.SPOKEN_LANGUAGES);
                return languages && languages.length > 0 ? languages : [];
            }
            return [];
        },
        get communicationLanguages() {
            if (fth.check(FeatureType.CUSTOMER)) {
                const referenceData = JSON.parse(window.localStorage.getItem(StorageKey.REFERENCE_DATA));
                const supportLanguages = referenceData && referenceData.some(r => r.name === 'CommunicationLanguageType') ? referenceData.filter(r => r.name === 'CommunicationLanguageType')[0].options : [];
                const languages = supportLanguages.filter(l1 => getArrayValue(TenantKey.COMMUNICATION_LANGUAGES).some(l2 => l1.key === l2));
                return languages && languages.length > 0 ? languages : [];
            }
            return [];
        },
        get colorOptions() {
            if (fth.check(FeatureType.CUSTOMER)) {
                const colorOptionText = getValue(TenantKey.PATIENT_COLOR_OPTIONS);
                const colorOptions = colorOptionText ? JSON.parse(getValue(TenantKey.PATIENT_COLOR_OPTIONS)) : null;
                return colorOptions && colorOptions.length > 0 ? colorOptions : [];
            }
            return [];
        }
    },
    appointment: {
        get dayViewGotoOptions() {
            if (fth.check(FeatureType.APPOINTMENT)) {
                const options = getAppointmentGotoOptions(TenantKey.APPOINTMENTS_DAYVIEW_GOTO_OPTIONS);
                return options;
            }
            return {};
        },
        get weekViewGotoOptions() {
            if (fth.check(FeatureType.APPOINTMENT)) {
                const options = getAppointmentGotoOptions(TenantKey.APPOINTMENTS_WEEKVIEW_GOTO_OPTIONS);
                return options;
            }
            return {};
        },
        get isGroupAppointmentEnabled() {
            if (fth.check(FeatureType.APPOINTMENT)) {
                const isEnabled = getBooleanValue(TenantKey.GROUP_APPOINTMENTS_ENABLED, false);
                return isEnabled;
            }
            return false;
        },
        get isPretestFileUploadEnabled() {
            if (fth.check(FeatureType.APPOINTMENT)) {
                const isEnabled = getBooleanValue(TenantKey.PRETEST_FILE_UPLOAD_ENABLED, false);
                return isEnabled;
            }
            return false;
        },
    },
    purchase: {
        get governmentProgramCodes() {
            if (fth.check(FeatureType.INVOICE)) {
                const governmentPrograms = getArrayValue(TenantKey.GOVERNMENT_PROGRAMS);
                return governmentPrograms && governmentPrograms.length > 0 ? governmentPrograms : [];
            }
            return [];
        },
        get isUnitPriceUpdateEnabled() {
            if (fth.check(FeatureType.INVOICE)) {
                const isEnabled = getBooleanValue(TenantKey.PURCHASE_UNIT_PRICE_UPDATE_ENABLED, false);
                return isEnabled;
            }
            return false;
        },
        get quoteExpireInDays() {
            if (fth.check(FeatureType.INVOICE)) {
                const days = parseInt(getValue(TenantKey.PURCHASE_QUOTE_EXPIRE_IN_DAYS), 10);
                return !isNaN(days) && days >= 0 ? days : 30;
            }
            return 99999;
        },
        get autoDisplayPicker() {
            if (fth.check(FeatureType.INVOICE)) {
                const isEnabled = getBooleanValue(TenantKey.PURCHASE_AUTO_DISPLAY_PICKER, true);
                return isEnabled;
            }
            return false;
        },
        get isMultiplePaymentsEnabled() {
            if (fth.check(FeatureType.INVOICE)) {
                const mode = getValue(TenantKey.PURCHASE_PAYMENT_MULTIPLE_ENABLED);
                return !!mode && ['all', 'single'].some(m => m === mode)
            }
            return false;
        },
        get multiplePaymentsDefault() {
            if (fth.check(FeatureType.INVOICE)) {
                const mode = getValue(TenantKey.PURCHASE_PAYMENT_MULTIPLE_ENABLED);
                return this.isMultiplePaymentsEnabled ? (!!mode && ['all', 'single'].some(m => m === mode) ? mode : 'single') : null;
            }
            return null;
        },
        get appointmentInvoiceWarningEnabled() {
            if (fth.check(FeatureType.INVOICE)) {
                const isEnabled = getBooleanValue(TenantKey.PURCHASE_APPOINTMENT_INVOICE_WARNING_ENABLED, true);
                return isEnabled;
            }
            return false;
        },
    },
    workOrder: {
        get reorderReminderOptions() {
            if (fth.check(FeatureType.WORK_ORDER)) {
                const reorderReminder = getValue(TenantKey.WORK_ORDER_REORDER_REMINDER);
                return reorderReminder ? JSON.parse(reorderReminder) : [];
            }
            return [];
        },
        get autoWorkOrderCreate() {
            if (fth.check(FeatureType.WORK_ORDER)) {
                const isEnabled = getBooleanValue(TenantKey.PURCHASE_AUTO_WORK_ORDER_CREATE, true);
                return isEnabled;
            }
            return false;
        },
    },
    exam: {
        get isPaperMigrationEnabled() {
            if (fth.check(FeatureType.EXAM)) {
                const isEnabled = getBooleanValue(TenantKey.PAPER_MIGRATION_ENABLED, false);
                return isEnabled === true;
            }
            return false;
        },
        get deviceSyncClearBeforeImport() {
            if (fth.check(FeatureType.EXAM)) {
                const clearBeforeImport = getBooleanValue(TenantKey.EXAM_DEVICE_SYNC_CLEAR_BEFORE_IMPORT, false);
                return clearBeforeImport === true;
            }
            return false;
        },
        get autoCompleteDays() {
            if (fth.check(FeatureType.EXAM)) {
                const autoCompleteDays = parseInt(getValue(TenantKey.DAYS_EXAM_AUTOCOMPLETE), 10);
                return !isNaN(autoCompleteDays) && autoCompleteDays >= 0 ? autoCompleteDays : 30;
            }
            return 99999;
        },
        get permanentCompletionEnabled() {
            if (fth.check(FeatureType.EXAM)) {
                const examPermanentCompletion = getBooleanValue(TenantKey.EXAM_PERMANENT_COMPLETION_ENABLED, false);
                return examPermanentCompletion;
            }
            return false;
        },
    },
    medicalReport: {
        get signaturePinRequired() {
            if (fth.check(FeatureType.EXAM)) {
                const isRequired = getBooleanValue(TenantKey.MEDICAL_REPORT_SIGNATURE_PIN_REQUIRED, true);
                return isRequired === true;
            }
            return false;
        }
    },
    referral: {
        get signaturePinRequired() {
            if (fth.check(FeatureType.EXAM)) {
                const isRequired = getBooleanValue(TenantKey.REFERRAL_SIGNATURE_PIN_REQUIRED, true);
                return isRequired === true;
            }
            return false;
        }
    },
    prescription: {
        get isDraftPrintEnabled() {
            if (fth.check(FeatureType.EXAM)) {
                const isEnabled = getBooleanValue(TenantKey.PRESCRIPTION_DRAFT_PRINT_ENABLED, false);
                return isEnabled === true;
            }
            return false;
        },
        get expireInMonths() {
            if (fth.check(FeatureType.EXAM)) {
                const expireInMonths = parseInt(getValue(TenantKey.PRESCRIPTION_EXPIRE_IN_MONTHS, '12'), 10);
                return expireInMonths;
            }
            return 12;
        },
        get bypassFinalize() {
            if (fth.check(FeatureType.EXAM)) {
                const bypassFinalize = getBooleanValue(TenantKey.PRESCRIPTION_BYPASS_FINALIZE, false);
                return bypassFinalize;
            }
            return false;
        },
        get bypassSignaturePin() {
            if (fth.check(FeatureType.EXAM)) {
                const bypassSignaturePin = getBooleanValue(TenantKey.PRESCRIPTION_BYPASS_SIGNATURE_PIN, false);
                return bypassSignaturePin;
            }
            return false;
        },
    },
    recall: {
        get isEnabled() {
            if (fth.check(FeatureType.EXAM)) {
                const mode = getValue(TenantKey.RECALL_MODE);
                return !!mode && ['manual', 'endOfDay', 'automated'].some(m => m === mode)
            }
            return false;
        },
        get mode() {
            if (fth.check(FeatureType.EXAM)) {
                const mode = getValue(TenantKey.RECALL_MODE);
                return mode && ['manual', 'endOfDay', 'automated'].some(m => m === mode) ? mode : 'manual';
            }
            return 'manual';
        },
        get size() {
            if (fth.check(FeatureType.EXAM)) {
                const size = parseInt(getValue(TenantKey.RECALL_SIZE), 10);
                return !isNaN(size) && size >= 0 ? size : 20;
            }
            return 0;
        },
        get yearsInRear() {
            if (fth.check(FeatureType.EXAM)) {
                const years = parseInt(getValue(TenantKey.RECALL_YEARS_IN_REAR), 10);
                return !isNaN(years) && years >= 0 ? years : 4;
            }
            return 0;
        },
        get daysInAdvance() {
            if (fth.check(FeatureType.EXAM)) {
                const days = parseInt(getValue(TenantKey.RECALL_DAYS_IN_ADVANCED), 10);
                return !isNaN(days) && days >= 0 ? days : 0;
            }
            return 0;
        },
        get defaultDelayInDays() {
            if (fth.check(FeatureType.EXAM)) {
                const days = parseInt(getValue(TenantKey.RECALL_SEND_DELAY), 10);
                return !isNaN(days) && days >= 0 ? days : 3;
            }
            return 0;
        },
        get defaultDelaySendTime() {
            if (fth.check(FeatureType.EXAM)) {
                const time = getValue(TenantKey.RECALL_SEND_TIME);
                return time;
            }
            return '00:00';
        },
        get maxAttempt() {
            if (fth.check(FeatureType.EXAM)) {
                const value = parseInt(getValue(TenantKey.RECALL_MAX_ATTEMPTS), 10);
                return value && !isNaN(value) ? value : 2;
            }
            return 0;
        },
        get followUpReminderOptions() {
            if (fth.check(FeatureType.EXAM)) {
                const followUpReminder = getValue(TenantKey.RECALL_FOLLOWUP_REMINDER);
                return followUpReminder ? JSON.parse(followUpReminder) : [];
            }
            return [];
        },
        getScheduledSendDateTimeLocal(localDate) {
            if (fth.check(FeatureType.EXAM)) {
                const mDate = isMoment(localDate) ? localDate.clone().startOf('day') : moment().startOf('day');
                const defaultDelayInDays = parseInt(getValue(TenantKey.RECALL_SEND_DELAY), 10);
                const defaultDelaySendTime = getValue(TenantKey.RECALL_SEND_TIME);
                const recallDay = mDate.clone().add(defaultDelayInDays, 'days');
                const recallTime = moment(defaultDelaySendTime, 'HH:mm');
                const scheduledSendDateTimeLocal = recallDay.set({ hour: recallTime.hour(), minute: recallTime.minute(), second: 0 });

                return scheduledSendDateTimeLocal;
            }
            return null;
        },
    },
    review: {
        get isEnabled() {
            if (fth.check(FeatureType.REVIEW)) {
                const mode = getValue(TenantKey.REVIEW_MODE);
                const url = getValue(TenantKey.REVIEW_URL);
                return !!mode && !!url && ['manual', 'endOfDay', 'automated'].some(m => m === mode)
            }
            return false;
        },
        get mode() {
            if (fth.check(FeatureType.REVIEW)) {
                const mode = getValue(TenantKey.REVIEW_MODE);
                return mode && ['manual', 'endOfDay', 'automated'].some(m => m === mode) ? mode : 'disabled';
            }
            return 'disabled';
        },
        get url() {
            if (fth.check(FeatureType.REVIEW)) {
                const url = getValue(TenantKey.REVIEW_URL);
                return url;
            }
            return '';
        },
        get defaultDelayInDays() {
            if (fth.check(FeatureType.REVIEW)) {
                const days = parseInt(getValue(TenantKey.REVIEW_SEND_DELAY), 10);
                return !isNaN(days) && days >= 0 ? days : 3;
            }
            return 0;
        },
        get defaultDelaySendTime() {
            if (fth.check(FeatureType.REVIEW)) {
                const time = getValue(TenantKey.REVIEW_SEND_TIME);
                return time;
            }
            return '00:00';
        },
        get sendMultiplePerPatient() {
            if (fth.check(FeatureType.REVIEW)) {
                const sendMultiple = getBooleanValue(TenantKey.REVIEW_SEND_MULTIPLE_PER_PATIENT);
                return sendMultiple === true;
            }
            return false;
        },
        isWithinRange(localDate) {
            if (fth.check(FeatureType.REVIEW)) {
                const lookBack = parseInt(getValue(TenantKey.REVIEW_LOOKBACK_PERIOD_IN_MONTHS), 10) * -1;
                const mDate = isMoment(localDate) ? localDate.clone().startOf('day') : moment(localDate).startOf('day');
                const today = moment().startOf('day');

                return today.add((isNaN(lookBack) ? -3 : lookBack), 'months').isBefore(mDate);
            }
            return false;
        },
        getScheduledSendDateTimeLocal(localDate) {
            if (fth.check(FeatureType.REVIEW)) {
                const mDate = isMoment(localDate) ? localDate.clone().startOf('day') : moment().startOf('day');
                const defaultDelayInDays = parseInt(getValue(TenantKey.REVIEW_SEND_DELAY), 10);
                const defaultDelaySendTime = getValue(TenantKey.REVIEW_SEND_TIME);
                const reviewDay = mDate.clone().add(defaultDelayInDays, 'days');
                const reviewTime = moment(defaultDelaySendTime, 'HH:mm');
                const scheduledSendDateTimeLocal = reviewDay.set({ hour: reviewTime.hour(), minute: reviewTime.minute(), second: 0 });

                return scheduledSendDateTimeLocal;
            }
            return '00:00';
        }
    },
    endOfDay: {
        get isEnabled() {
            if (fth.check(FeatureType.BASIC_REPORT)) {
                const isEnabled = getBooleanValue(TenantKey.END_OF_DAY_ENABLED);
                return isEnabled === true;
            }
            return false;
        },
        get summarySendTo() {
            if (fth.check(FeatureType.BASIC_REPORT)) {
                const value = getValue(TenantKey.END_OF_DAY_SUMMARY_SEND_TO);
                return JSON.parse(value);
            }
            return null;
        },
        get associateSummarySendTo() {
            if (fth.check(FeatureType.BASIC_REPORT)) {
                const value = getValue(TenantKey.END_OF_DAY_ASSOCIATE_SUMMARY_SEND_TO);
                return JSON.parse(value);
            }
            return null;
        },
    },
    onlineBooking: {
        get isEnabled() {
            if (fth.check(FeatureType.ONLINE_BOOKING)) {
                const isEnabled = getBooleanValue(TenantKey.ONLINE_BOOKING_ENABLED);
                return isEnabled === true;
            }
            return false;
        },
        get cutoffHours() {
            if (fth.check(FeatureType.ONLINE_BOOKING)) {
                const value = parseInt(getValue(TenantKey.ONLINE_BOOKING_TIMESLOT_CUTOFF_HOURS), 10);
                return value;
            }
            return 3;
        },
        get googlePlaceId() {
            if (fth.check(FeatureType.ONLINE_BOOKING)) {
                const value = getValue(TenantKey.ONLINE_BOOKING_GOOGLE_PLACE_ID);
                return value;
            }
            return '';
        },
    },
}

export const getFormattedPhoneNumber = (value) => {
    return getFormattedValue(TenantKey.PHONE_NUMBER_FORMAT, value, 'PhoneBrackets');
}

export const getFormattedLongDate = (value, includeTime = false) => {
    const date = getFormattedValue(TenantKey.LONG_DATE_FORMAT, value, 'ShortDayOfWeekDate');

    if (!!includeTime) {
        return `${date} @ ${value.format('h:mm a')}`;
    }

    return date;
}

export const getFormattedShortDate = (value, includeTime = false) => {
    const date = getFormattedValue(TenantKey.SHORT_DATE_FORMAT, value, 'DashYearMonthDay');

    if (!!includeTime) {
        return `${date} @ ${value.format('h:mm a')}`;
    }

    return date;
}

const getAppointmentGotoOptions = (key) => {
    const array = getArrayValue(key);
    const options = array && array.length > 0 ? array.map(a => {
        if (a) {
            const parts = a.split(',');

            if (parts && parts.length === 3) {
                let text;
                const number = parseInt(parts[0], 10);
                const unit = parts[1];
                const addDay = parseInt(parts[2], 10);
                const key = `_${number}${unit}`;

                if (!isNaN(number) && ['week', 'month', 'year'].some(u => unit === u) && !isNaN(addDay)) {
                    switch (unit.toLowerCase()) {
                        case 'week':
                            text = `${number} wk`;
                            break;

                        case 'month':
                            text = `${number} mo`;
                            break;

                        case 'year':
                            text = `${number} yr`;
                            break;

                        default:
                            break;
                    }

                    return { key, text, number, unit, addDay }
                }
            }
        }
        return null;
    }).filter(o => !!o) : null;

    return options && options.length > 0 ? options : [];
}

const getValue = (key, defaultValue) => {
    const settings = window.localStorage.getItem(StorageKey.SETTINGS) ? JSON.parse(window.localStorage.getItem(StorageKey.SETTINGS)) : [];
    const setting = settings.filter(setting => setting.key === key)[0];

    return setting && setting.value ? setting.value : defaultValue;
}

const getFormattedValue = (key, value, defaultFormat) => {
    const settings = window.localStorage.getItem(StorageKey.SETTINGS) ? JSON.parse(window.localStorage.getItem(StorageKey.SETTINGS)) : [];
    const formatSetting = settings.filter(setting => setting.key === key)[0];

    if (formatSetting) {
        return fh.apply(value, formatSetting.value, value);
    }

    return fh.apply(value, defaultFormat, value);
}

const getBooleanValue = (key, defaultValue) => {
    const settings = window.localStorage.getItem(StorageKey.SETTINGS) ? JSON.parse(window.localStorage.getItem(StorageKey.SETTINGS)) : [];
    const booleanSetting = settings.filter(setting => setting.key === key)[0];

    if (booleanSetting) {
        return booleanSetting.value === 'true';
    }

    return defaultValue;
}

const getArrayValue = (key) => {
    const settings = window.localStorage.getItem(StorageKey.SETTINGS) ? JSON.parse(window.localStorage.getItem(StorageKey.SETTINGS)) : [];
    const arraySetting = settings.filter(setting => setting.key === key)[0];

    if (arraySetting && arraySetting.value) {
        if (arraySetting.value.indexOf('|') >= 0) {
            return arraySetting.value.split('|');
        }
        else {
            return [arraySetting.value];
        }
    }

    return [];
}